// BOT-1108 - the product modal is deep-linkable as a PATH segment on the Selection route it
// already lives on: `/:storeSlug?/b/:bottleid/:productSlug(.*)?`, so `/acme/b/1814/hot-chic`.
//
// Two things make that safe, and neither is optional:
//
//   1. The route param is `(.*)`, so nothing under /b/:bottleid/ can reach the `/:pathMatch(.*)`
//      catch-all. NotFound is not an inert 404 - it matches the last path segment against the
//      merchant's franchise and can push the customer into a different store, and on a failed
//      lookup it deletes the auth token and hard-reloads the same unmatched URL. A junk product
//      link has to render the store, and `(.*)` is what guarantees it.
//   2. `isSamePathNavigation` compares page identity rather than raw paths (see below). Opening and
//      closing a modal changes the path now, and treating either as a page change would blank
//      in-page state and double-count analytics on every single product a customer looks at.
export const PRODUCT_PATH_PARAM = 'productSlug'

// A navigation that only changes the product segment, the query or the hash stays on the same page:
// the view is never torn down and no new content loads. Treating one as a page change blanks
// in-page state (the "View our other menus" carousel is gated on SET_MAIN_CONTENT_LOADED) and
// double-counts analytics, since a modal navigates on both open and close.
//
// `from.name` is unset on the very first navigation, which is a genuine page view even when the
// path happens to match the start location.
//
// Identity is the route NAME plus every param except the product slug. Comparing raw paths - which
// is what this did while the deep link was a query param - now reports a page change for every
// modal open and close. Unnamed records (the catch-all, /privacy, /terms-of-service) keep path
// identity, and since `from` must be named to get past the first check, they can never take the
// param-based branch.
function pageIdentity(route) {
  if (!route) return null
  if (!route.name) return `path:${route.path || ''}`

  const params = route.params || {}
  const parts = Object.keys(params)
    .filter((key) => key !== PRODUCT_PATH_PARAM)
    .sort()
    .map((key) => `${key}=${Array.isArray(params[key]) ? params[key].join('/') : params[key]}`)

  return `name:${String(route.name)}|${parts.join('&')}`
}

export function isSamePathNavigation(to, from) {
  if (!from || !from.name) return false
  const identity = pageIdentity(to)
  return identity != null && identity === pageIdentity(from)
}

export function readProductSlug(route) {
  const raw = route?.params?.[PRODUCT_PATH_PARAM]
  // vue-router hands back an array for a repeatable param, and an absent optional param resolves to
  // an empty string rather than undefined.
  const slug = Array.isArray(raw) ? raw[0] : raw
  if (slug == null || slug === '') return null
  return String(slug)
}

// Built literally from the route's own params rather than through `router.resolve({ name })`.
// Resolving by name drops `storeSlug` and the whole query string, which re-arms both global guards
// - the store-slug injection redirect and the persisted-UTM merge - on every modal open.
function selectionBasePath(route) {
  const params = route?.params || {}
  const storeSlug = params.storeSlug ? `/${params.storeSlug}` : ''
  return `${storeSlug}/b/${params.bottleid}`
}

function locationWithProduct(route, slug) {
  const base = selectionBasePath(route)
  return {
    path: slug ? `${base}/${slug}` : base,
    query: { ...(route?.query || {}) },
    hash: route?.hash || ''
  }
}

// Only ever called from the Selection route's modal hosts, but guard anyway: building a path out of
// `params.bottleid` on any other route would produce `/b/undefined`.
function onSelectionRoute(route) {
  return route?.name === 'Selection' && route?.params?.bottleid != null
}

// Opening pushes, so browser Back closes the modal. Swapping straight from one product to another
// replaces, so a browse-through-five-products session leaves one entry to back out of rather than
// five. Re-opening the product that is already in the URL does nothing at all, which is what makes
// it safe for a host to call this from a watcher that the navigation itself re-triggers.
export function openProductLink(router, route, slug) {
  if (!router || !slug || !onSelectionRoute(route)) return undefined

  const next = String(slug)
  const current = readProductSlug(route)
  if (current === next) return undefined

  const location = locationWithProduct(route, next)
  return current ? router.replace(location) : router.push(location)
}

// Strips only the product segment - every other query param is preserved, because closing a modal
// must not drop the attribution params the router persisted onto the URL.
//
// This ALWAYS replaces, and deliberately never calls `router.back()`. An earlier version popped
// history when `history.state.back` looked like this same page without a product, on the theory
// that it consumed the entry the open pushed. That is not safe in this app: a single storefront
// page load performs several navigations (observed: history.length 5, position 4 from one load),
// and two global guards in router/index.js actively redirect - the `slugAwareRouteNames` store-slug
// injection and the persisted-UTM merge. So `state.back` routinely does not describe the page the
// customer came from, and popping it ejected them out of checkout to `/` instead of dismissing
// the modal.
//
// Replacing costs nothing here: the open pushed an entry, so replacing it with the clean URL means
// browser Back still lands on whatever preceded the modal. Correct behaviour, no guessing.
export function closeProductLink(router, route) {
  if (!router || !onSelectionRoute(route)) return undefined
  if (readProductSlug(route) == null) return undefined

  return router.replace(locationWithProduct(route, null))
}
